import { Link } from "react-router-dom";
import SiteNav from "@/components/SiteNav";
import { Button } from "@/components/ui/button";
import CinematicBook from "@/components/CinematicBook";
import { useAuth } from "@/contexts/AuthContext";

const features = [
  {
    title: "Title & theme studio",
    body: "Generate 6 candidate titles, themes and genres from a single sentence pitch. Powered by Claude, Gemini or GPT.",
    test: "feat-title",
  },
  {
    title: "Distraction-free editor",
    body: "Page-by-page writing with continuous auto-save. Your manuscript is always safe.",
    test: "feat-editor",
  },
  {
    title: "AI co-author",
    body: "Stuck on a chapter? Ask the AI to draft the next 1,200 words in your book's voice.",
    test: "feat-ai",
  },
  {
    title: "Uniqueness analysis",
    body: "Get an originality report — cliché density, derivative phrases, suggestions to sharpen your voice.",
    test: "feat-uniqueness",
  },
  {
    title: "Manuscript export",
    body: "One click and you have a polished PDF and DOCX manuscript ready for editors and reviewers.",
    test: "feat-export",
  },
  {
    title: "Pre-launch + post-launch hub",
    body: "YouTube trailer, ad URLs, and AI-written posts for Facebook, WhatsApp and LinkedIn — all in one place.",
    test: "feat-publicity",
  },
];

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen" style={{ background: "var(--bf-bg)" }} data-testid="landing-page">
      <SiteNav variant="marketing" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://static.prod-images.emergentagent.com/jobs/e21241af-dc17-4ac1-b5d0-f39caea73c8a/images/55410c45e12b57513424c2e1a6867ae80550bc7bb92f2b3775f48e468dbc0dca.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 lg:pt-32 pb-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="label-eyebrow mb-6" data-testid="hero-eyebrow">The book writing lifecycle</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-8">
              From a single idea<br />to a published book —<br />
              <em className="italic" style={{ color: "var(--bf-primary)" }}>without leaving Bookforge.</em>
            </h1>
            <p className="text-lg mb-10 max-w-xl leading-relaxed" style={{ color: "var(--bf-text-muted)" }}>
              Pick a title, plot your chapters, write with AI, run an originality check, export your
              manuscript and launch with ready-to-post copy for Facebook, WhatsApp and LinkedIn.
            </p>
            <div className="flex gap-4">
              <Link to="/register" data-testid="hero-cta-start">
                <Button
                  className="text-white h-12 px-7 text-base"
                  style={{ background: "var(--bf-primary)" }}
                  data-testid="hero-start-writing-btn"
                >
                  Start writing — it&apos;s free
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-cta-signin">
                <Button variant="outline" className="h-12 px-7 text-base" data-testid="hero-signin-btn">
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <CinematicBook authorName={user?.name} />
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="mb-16 max-w-2xl">
          <div className="label-eyebrow mb-4">Everything you need</div>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight">
            One studio for the entire arc of writing a book.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((f) => (
            <div
              key={f.title}
              className="p-8 transition-all hover:-translate-y-1"
              style={{
                background: "var(--bf-surface)",
                border: "1px solid var(--bf-border)",
                borderRadius: "8px",
              }}
              data-testid={f.test}
            >
              <h3 className="font-serif text-2xl mb-3">{f.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--bf-text-muted)" }}>
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Flow */}
      <section id="flow" className="py-24" style={{ background: "var(--bf-surface)" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="label-eyebrow mb-4">The journey</div>
          <h2 className="font-serif text-4xl sm:text-5xl mb-14">
            Five steps from idea to launch day.
          </h2>
          <ol className="space-y-10">
            {[
              ["01", "Pitch and shape", "Tell us your idea in a sentence. We generate titles, themes, genres and a tagline."],
              ["02", "Write, chapter by chapter", "A clean editor, auto-save every keystroke, and an AI co-author on call."],
              ["03", "Check for originality", "Run an originality report on any chapter or the whole manuscript."],
              ["04", "Convert to manuscript", "Export as a beautifully typeset PDF or editor-ready DOCX."],
              ["05", "Promote your launch", "Publish a public book page, attach a YouTube trailer and generate share copy for FB, WhatsApp and LinkedIn."],
            ].map(([n, t, d]) => (
              <li key={n} className="grid grid-cols-12 gap-6 items-start" data-testid={`flow-step-${n}`}>
                <span className="col-span-2 font-serif text-5xl" style={{ color: "var(--bf-accent)" }}>{n}</span>
                <div className="col-span-10">
                  <h3 className="font-serif text-2xl mb-2">{t}</h3>
                  <p className="text-base leading-relaxed" style={{ color: "var(--bf-text-muted)" }}>{d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Publicity */}
      <section id="publicity" className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-2 gap-16 items-center">
        <img
          src="https://images.unsplash.com/photo-1763890965393-1cea435581ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB3cml0aW5nJTIwYXQlMjBkZXNrJTIwd2FybSUyMHN1bmxpZ2h0fGVufDB8fHx8MTc4MDA3MDI2M3ww&ixlib=rb-4.1.0&q=85"
          alt="Author at desk"
          className="w-full h-[520px] object-cover rounded-lg"
          style={{ border: "1px solid var(--bf-border)" }}
        />
        <div>
          <div className="label-eyebrow mb-4">Your public book page</div>
          <h2 className="font-serif text-4xl sm:text-5xl mb-6 leading-tight">
            bookforge.app/<em style={{ color: "var(--bf-primary)" }}>yourname</em>/<em style={{ color: "var(--bf-primary)" }}>yourbook</em>
          </h2>
          <p className="text-base mb-8 leading-relaxed" style={{ color: "var(--bf-text-muted)" }}>
            Every author gets a permanent landing page. Share one link that contains your book
            summary, your YouTube trailer, your ad call-to-action and your social handles.
          </p>
          <Link to="/register">
            <Button className="text-white h-12 px-7" style={{ background: "var(--bf-primary)" }} data-testid="publicity-cta">
              Claim your author page
            </Button>
          </Link>
        </div>
      </section>

      <footer className="py-12 border-t" style={{ borderColor: "var(--bf-border)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm" style={{ color: "var(--bf-text-muted)" }}>
          <div className="font-serif text-xl">Bookforge</div>
          <div>© 2026 Bookforge. Made for writers.</div>
        </div>
      </footer>
    </div>
  );
}
